import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { getClient } from 'lane-shared/apollo';
import { AttachmentResponse } from 'lane-shared/types/attachment';
import { convertToUUID } from 'uuid-encoding';

import { updatePMTaskMutation } from 'graphql-queries';
import { ClientTask } from 'graphql-query-contracts';

import { convertTeamToID } from './convertTeamToID';

type UsePmTaskUpdateParams = {
  task: ClientTask;
  refetch: () => Promise<unknown>;
  refetchAuditLogs: () => Promise<unknown>;
};

export function usePmTaskUpdate({
  task,
  refetch,
  refetchAuditLogs,
}: UsePmTaskUpdateParams) {
  const { t } = useTranslation();

  const refetchData = () => {
    refetch();
    refetchAuditLogs();
  };

  const onTaskUpdated = useCallback(
    async (fields: Record<string, unknown>) => {
      try {
        await getClient().mutate({
          mutation: updatePMTaskMutation,
          variables: {
            task: {
              id: convertToUUID(task?.id),
              assignee: task.assignee?._id,
              assigneeGroup: task.assigneeGroup
                ? task.assigneeGroup.map(group => group._id)
                : null,
              attachments: task.attachments,
              notes: task.notes,
              hours: task.hours,
              minutes: task.minutes,
              ...fields,
            },
          },
        });
        refetchData();
        await window?.Toast?.show(
          t(
            'web.admin.workOrder.preventiveMaintenance.task.details.toast.success'
          )
        );
      } catch (err) {
        throw new Error(String(err));
      }
    },
    [
      refetch,
      refetchAuditLogs,
      task?.id,
      task?.assignee,
      task?.assigneeGroup,
      task?.attachments,
      task?.notes,
      task?.hours,
      task?.minutes,
    ]
  );

  const updateTask = async (
    assignee: string | undefined,
    assigneeTeam: string[] | undefined,
    attachments: string[]
  ) => {
    const taskData = {
      id: convertToUUID(task?.id),
      assignee,
      assigneeGroup: assigneeTeam,
      attachments,
    };

    try {
      await getClient().mutate({
        mutation: updatePMTaskMutation,
        variables: {
          task: {
            ...taskData,
          },
        },
      });
      await refetch();
      window.Toast.show(
        t`web.admin.workOrder.preventiveMaintenance.task.details.toast.success`
      );
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err: any) {
      window.Toast.show(
        t`web.admin.workOrder.preventiveMaintenance.task.details.toast.error`
      );
    }
  };

  const handleAttachmentCreated = async (attachments: AttachmentResponse[]) => {
    await updateTask(
      task?.assignee?._id || '',
      convertTeamToID(task?.assigneeGroup || []),
      attachments.map(attachment => attachment.id)
    );
  };

  const handleAttachmentDeleted = async (attachmentId: string) => {
    const attachmentIds =
      task.attachments?.filter(id => id !== attachmentId) ?? [];

    await updateTask(
      task?.assignee?._id || '',
      convertTeamToID(task?.assigneeGroup || []),
      attachmentIds
    );
  };

  return {
    updateTask,
    refetchData,
    onTaskUpdated,
    handleAttachmentCreated,
    handleAttachmentDeleted,
  };
}
